export const PROTOCOL_CATEGORIES = [
  { id: 'new', title: 'Установил новые', short: 'Новые рекорды' },
  { id: 'improved', title: 'Обновил старые', short: 'Обновления' },
  { id: 'top_two', title: 'Ворвался в двойку', short: 'Вход в двойку' },
  { id: 'entered', title: 'Ворвался в тройку', short: 'Вход в тройку' },
  { id: 'outside', title: 'Динамика четвёрки+', short: 'Четвёрка+' },
];

export function protocolTitle(protocol) {
  return protocol.title || (protocol.opponent ? `Вашингтон — ${protocol.opponent}` : 'Новый протокол');
}

export function protocolDate(date) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '')) return 'Дата не указана';
  const value = new Date(`${date}T12:00:00Z`);
  return Number.isFinite(value.getTime())
    ? new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(value)
    : 'Дата не указана';
}

export function protocolCounts(entries = []) {
  return Object.fromEntries(PROTOCOL_CATEGORIES.map(({ id }) => [id, entries.filter((entry) => entry.category === id).length]));
}

export function protocolEntryCount(entries = []) {
  return Object.values(protocolCounts(entries)).reduce((total, count) => total + count, 0);
}

// Import proposes rows only. Match details and publication always belong to the editor.
export function parseProtocolText(text) {
  if (typeof text !== 'string' || text.length > 100000) throw new Error('Текст должен быть не длиннее 100 000 символов');
  const entries = [], warnings = [], declared = new Map(), seasonLines = [];
  let category = null;
  let inSeasonSummary = false;
  const headings = [
    ['new', /^(?:установил\s+новые|новые\s+рекорды)(?:\s*:\s*\d*)?\s*$/],
    ['improved', /^(?:обновил\s+старые|обновления)(?:\s*:\s*\d*)?\s*$/],
    ['top_two', /^(?:ворвался\s+в\s+двойку|вход\s+в\s+двойку)(?:\s*:\s*\d*)?\s*$/],
    ['podium', /^движение\s+в\s+тройке(?:\s*:\s*\d*)?\s*$/],
    ['entered', /^(?:ворвался\s+в\s+тройку|вход\s+в\s+тройку)(?:\s*:\s*\d*)?\s*$/],
    ['outside', /^(?:динамика\s+четверки\+?|четверка\s*\+)(?:\s*:\s*\d*)?\s*$/],
  ];
  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.replace(/[\u0000-\u0008\u000b-\u001f]/g, '').trim();
    if (!line) continue;
    const normalized = line.toLowerCase().replace(/ё/g, 'е');
    if (/^итого(?:\s|:).*за\s+сезон/u.test(normalized)) inSeasonSummary = true;
    if (inSeasonSummary) { seasonLines.push(line); continue; }
    const group = headings.find(([, pattern]) => pattern.test(normalized));
    if (group) {
      category = group[0];
      if (category === 'podium') {
        if (!warnings.includes('Раздел «Движение в тройке» пропущен: он больше не входит в протокол.')) {
          warnings.push('Раздел «Движение в тройке» пропущен: он больше не входит в протокол.');
        }
        continue;
      }
      const count = line.match(/:\s*(\d+)\s*$/);
      if (count) declared.set(category, Number(count[1]));
      continue;
    }
    if (/^протокол\s+(?:рекордов|игры)/iu.test(line)) { category = null; continue; }
    if (category === 'podium') continue;
    if (!/^[№#]/u.test(line) || !category) {
      warnings.push(`Не распознана строка: ${line.slice(0, 180)}`);
      category = null;
      continue;
    }
    // Each marker starts a separate entry; a comma list/range shares its following text.
    for (const chunk of line.split(/[,;]?\s*(?=[№#]\s*\d)/u).filter(Boolean)) {
      const match = chunk.match(/^[№#]\s*(\d+(?:\s*[-–]\s*\d+){0,2}(?:\s*,\s*\d+(?:\s*[-–]\s*\d+){0,2})*)(.*)$/u);
      if (!match || /^\s*[-–,]\s*[-–,\d]/u.test(match[2])) {
        warnings.push(`Не распознаны номера: ${chunk.slice(0, 180)}`);
        continue;
      }
      const ids = [];
      let valid = true;
      for (const value of match[1].split(',')) {
        const token = value.replace(/\s/g, '').replace(/–/g, '-');
        const parts = token.split('-').map(Number);
        if (parts.some((part) => !Number.isSafeInteger(part) || part < 1)) { valid = false; break; }
        if (parts.length === 1) ids.push(parts[0]);
        else if (/^\d-\d-\d$/.test(token)) ids.push(Number(token.replace(/-/g, '')));
        else if (parts.length === 2 && parts[1] >= parts[0]) {
          if (parts[1] - parts[0] + 1 + entries.length + ids.length > 200) throw new Error('В одном протоколе может быть не больше 200 строк');
          for (let id = parts[0]; id <= parts[1]; id += 1) ids.push(id);
        } else { valid = false; break; }
      }
      if (!valid) { warnings.push(`Проверьте номера: ${match[1]}`); continue; }
      if (entries.length + ids.length > 200) throw new Error('В одном протоколе может быть не больше 200 строк');
      const entryText = match[2].replace(/^\s*[.:—–-]?\s*/u, '').replace(/[,;]\s*$/u, '').trim();
      entries.push(...ids.map((recordId) => ({ category, recordId, text: entryText, note: '' })));
    }
  }
  const counts = protocolCounts(entries);
  for (const [id, count] of declared) {
    if (count !== counts[id]) warnings.push(`«${PROTOCOL_CATEGORIES.find((item) => item.id === id).title}»: в заголовке ${count}, распознано ${counts[id]}`);
  }
  if (!entries.length) warnings.push('Строки не найдены. Используйте названия разделов и номера с символом №.');
  const seasonSummary = seasonLines.join('\n');
  if (seasonSummary.length > 4000) throw new Error('Итоги за сезон должны быть не длиннее 4 000 символов');
  return { entries, warnings, seasonSummary };
}
